import { useMemo, type ReactElement, type ReactNode, Children } from 'react';
import type { GroupBase } from 'react-select';

import { type StratoLogger } from '../../../core/sdk/sdk-logger.js';
import type { BehaviorTrackingProps } from '../../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../../core/types/data-props.js';
import { StylingProps } from '../../../core/types/styling-props.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../../core/utils/can-receive-default-text-format.js';
import { extractBehaviorTrackingProps } from '../../../core/utils/extract-behavior-tracking-props.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import type { SelectContentProps } from '../SelectContent.js';
import {
  SelectGroupLabel,
  SelectGroup,
  type SelectGroupProps,
} from '../SelectGroup.js';
import { _isSelectOptionElement, SelectOptionProps } from '../SelectOption.js';
import { SelectPrefix } from '../SelectPrefix.js';
import { SelectSuffix } from '../SelectSuffix.js';
import type { OptionOrGroup, BaseOption, BaseGroup } from '../types.js';

type Writeable<T> = { -readonly [P in keyof T]: T[P] };

/** Type guard helper to identify an element as an Option */
export function _isOption<OptionValue>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  optionOrGroup: any,
): optionOrGroup is BaseOption<OptionValue> {
  if (optionOrGroup.value !== undefined) {
    return true;
  }
  return false;
}

/** Type guard helper to identify an element as a Group */
export function _isGroup<OptionValue>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  optionOrGroup: any,
): optionOrGroup is GroupBase<OptionValue> {
  if (Array.isArray(optionOrGroup.options)) {
    return true;
  }
  return false;
}

/**
 * Helper function that will extract the options data from the option
 * child passed.
 */
function extractOptionData<OptionValue>(
  optionElement: ReactElement<SelectOptionProps<OptionValue>>,
): BaseOption<OptionValue> {
  const optionSlots: {
    prefix: {
      Prefix: ReactNode | null;
      componentProps?: StylingProps & DataTestId & BehaviorTrackingProps;
    };
    Label: ReactNode[] | null;
    suffix: {
      Suffix: ReactNode | null;
      componentProps?: StylingProps & DataTestId & BehaviorTrackingProps;
    };
  } = {
    prefix: { Prefix: null },
    Label: null,
    suffix: { Suffix: null },
  };

  Children.forEach(optionElement.props.children, (child) => {
    if (isSlot(child, SelectPrefix)) {
      optionSlots.prefix.Prefix = child.props.children;
      optionSlots.prefix.componentProps = {
        className: child.props.className,
        style: child.props.style,
        'data-testid': child.props['data-testid'],
        ...extractBehaviorTrackingProps(child.props),
      };
    } else if (isSlot(child, SelectSuffix)) {
      optionSlots.suffix.Suffix = child.props.children;
      optionSlots.suffix.componentProps = {
        className: child.props.className,
        style: child.props.style,
        'data-testid': child.props['data-testid'],
        ...extractBehaviorTrackingProps(child.props),
      };
    } else {
      optionSlots.Label = optionSlots.Label ?? [];
      if (child !== null) {
        optionSlots.Label.push(child);
      }
    }
  });

  return {
    value: optionElement.props.value,
    prefix: {
      component: optionSlots.prefix.Prefix,
      componentProps: optionSlots.prefix.componentProps,
    },
    label: optionSlots.Label,
    suffix: {
      component: optionSlots.suffix.Suffix,
      componentProps: optionSlots.suffix.componentProps,
    },
    isDisabled: optionElement.props.disabled,
    textValue: optionElement.props.textValue,
    'aria-label': optionElement.props['aria-label'],
    className: optionElement.props.className,
    style: optionElement.props.style,
    'data-testid': optionElement.props['data-testid'],
    ...extractBehaviorTrackingProps(optionElement.props),
  };
}

/**
 * @internal
 */
export function safeStringify(value: unknown): string {
  try {
    if (typeof value === 'string') {
      return value;
    }
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

function reportNoStringifyableValueError(
  value: unknown,
  logger?: StratoLogger,
) {
  logger?.warn('Failed to stringify value.', value);
}

/**
 * Helper function to get the stringified label of an option (used for filtering).
 * If there is a textValue defined, we use that as the value. Otherwise, we try to
 * stringify the value and the label, if possible. If that fails, we use the fallbackLabel.
 */
function getOptionLabel<OptionValue>(
  option: BaseOption<OptionValue>,
  fallbackLabel: string,
): string {
  if (option.textValue) {
    return option.textValue;
  }

  const labelIsStringifyable = _canReceiveDefaultTextFormat(option.label);
  const stringifiedValue = safeStringify(option.value);

  if (labelIsStringifyable && stringifiedValue) {
    return `${option.label} ${stringifiedValue}`;
  }

  if (labelIsStringifyable) {
    return `${option.label}`;
  }

  if (stringifiedValue) {
    return stringifiedValue;
  }

  return fallbackLabel;
}

/**
 * Helper function to get the stringified value of an option.
 * If there is a textValue defined, we use that as the value
 * otherwise we stringify the value, if that fails we use the
 * label, if possible, and the fallbackValue as a last resort.
 */
function getOptionValue<OptionValue>(
  option: BaseOption<OptionValue>,
  fallbackValue: string,
  logger?: StratoLogger,
): string {
  if (option.textValue) {
    return option.textValue;
  }

  const stringifiedValue = safeStringify(option.value);
  if (stringifiedValue) {
    return stringifiedValue;
  }

  const labelIsStringifyable = _canReceiveDefaultTextFormat(option.label);

  if (labelIsStringifyable) {
    return `${option.label}`;
  }

  reportNoStringifyableValueError(option.value, logger);

  return fallbackValue;
}

/**
 * Helper function that will extract the Group data from the group element
 * passed.
 */
function extractGroupData<OptionValue>(
  groupElement: ReactElement<SelectGroupProps>,
  groupIndex: number,
  logger?: StratoLogger,
): BaseGroup<OptionValue> {
  const {
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  } = groupElement.props;

  const group: BaseGroup<OptionValue> = {
    label: undefined,
    options: [],
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...extractBehaviorTrackingProps(groupElement.props),
  };

  Children.forEach(groupElement?.props.children, (child, optionIndex) => {
    if (isSlot(child, SelectGroupLabel)) {
      /*
       * TODO: This should be a ReactNode but the overlap with the GroupBase does not allow that.
       * We might need to fix the typing here, as it is no rendering issue whatsoever.
       */
      group.label = child.props.children as string;
      group.labelProps = {
        className: child.props.className,
        style: child.props.style,
        'data-testid': child.props['data-testid'],
        ...extractBehaviorTrackingProps(child.props),
      };
    }

    if (_isSelectOptionElement<OptionValue>(child)) {
      const option = extractOptionData<OptionValue>(child);
      // Since we are using a ReactNode[] as a label here instead of a string
      // we run into keying issues in the react-select internal.
      // With this fix, we make sure that the toString of the label array will
      // produce a valid string, avoiding duplicate key issues.
      // https://github.com/JedWatson/react-select/blob/53b85346bb7defd937257b0daf9d6993ef20a59a/packages/react-select/src/Select.tsx#L1823
      if (option.label) {
        const stringifiedLabel = getOptionLabel<OptionValue>(
          option,
          `group-${groupIndex}-label-${optionIndex}`,
        );
        option.label.toString = () => stringifiedLabel;
      }

      if (!option.textValue) {
        const stringifiedValue = getOptionValue<OptionValue>(
          option,
          `group-${groupIndex}-label-${optionIndex}`,
          logger,
        );
        option.textValue = stringifiedValue;
      }

      if (option !== null) {
        group.options.push(option);
      }
    }
  });

  return group;
}

/**
 * Helper hook that will extract Options or Groups from the Content children
 * and return a valid options or group array for react-select
 *
 * @internal
 */
export function useSelectOptions<OptionValue>(
  contentSlot: ReactElement<SelectContentProps> | null,
  logger?: StratoLogger,
): {
  optionsOrGroups: OptionOrGroup<BaseOption<OptionValue>>;
  optionsHavePrefix: boolean;
  allOptions: BaseOption<OptionValue>[];
} {
  // FIXME: We are always recreating the options array, which adds to additional
  // rerenders to the select whenever the parent of the Select updates.
  return useMemo(() => {
    const optionsOrGroups: Writeable<OptionOrGroup<BaseOption<OptionValue>>> =
      [];

    // Define if any of the options have a prefix or a suffix, as this will be
    // necessary to know for the MenuList and individual options to make sure
    // options without prefixes or suffixes also get the correct alignments;
    let optionsHavePrefix = false;
    const allOptions: BaseOption<OptionValue>[] = [];

    Children.forEach(contentSlot?.props.children, (child, childIndex) => {
      // If the child is an option, extract the data and add it to the collected
      // list.
      if (_isSelectOptionElement<OptionValue>(child)) {
        const option = extractOptionData<OptionValue>(child);
        // Since we are using a ReactNode[] as a label here instead of a string
        // we run into keying issues in the react-select internal.
        // With this fix, we make sure that the toString of the label array will
        // produce a valid string, avoiding duplicate key issues.
        // https://github.com/JedWatson/react-select/blob/53b85346bb7defd937257b0daf9d6993ef20a59a/packages/react-select/src/Select.tsx#L1823
        if (option.label) {
          const stringifiedLabel = getOptionLabel<OptionValue>(
            option,
            `label-${childIndex}`,
          );
          option.label.toString = () => stringifiedLabel;
        }

        if (!option.textValue) {
          const stringifiedValue = getOptionValue<OptionValue>(
            option,
            `value-${childIndex}`,
            logger,
          );
          option.textValue = stringifiedValue;
        }

        if (option !== null) {
          optionsOrGroups.push(option);
          // Check if the option has a prefix and set the global setting accordingly
          if (option.prefix.component !== null) {
            optionsHavePrefix = true;
          }
        }
        allOptions.push(option);
      }
      // If the child is a group, extract the group data from the child and add it to
      // collected list.
      if (isSlot(child, SelectGroup)) {
        const group = extractGroupData<OptionValue>(child, childIndex, logger);
        if (group !== null) {
          optionsOrGroups.push(group);
        }

        if (group.options.length > 20_000) {
          logger?.warn(
            `Group with label "${group.label}" has more than 20,000 options. This can lead to a slow and unresponsive UI.`,
          );
        }

        // As a spread push (allOptions.push(...group.options)) might lead to an exceeded call stack,
        // we add every single option of the group to the allOptions array.
        // This is more performant than using allOptions.concat(group.options) which would create
        // a new array every iteration of a group.
        for (const element of group.options) {
          allOptions.push(element);
        }

        // Only check if optionsHavePrefix is still false. If it is already true, there is no
        // need to check the nested groups again.
        if (optionsHavePrefix === false) {
          const nestedOptionsHavePrefix = group.options.some(
            (option) => option.prefix.component !== null,
          );
          if (nestedOptionsHavePrefix) {
            optionsHavePrefix = true;
          }
        }
      }
    });

    return { optionsOrGroups, optionsHavePrefix, allOptions };
  }, [contentSlot, logger]);
}
